import { AppShell, Button, Center, Group, Loader, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';

import { unwrap } from './api/client';
import { me } from './api/gen';
import { Wordmark } from './components/Wordmark';
import { useSession } from './lib/session';
import { ShelfPage } from './pages/ShelfPage';
import { SignInPage } from './pages/SignInPage';
import { UsersPage } from './pages/UsersPage';

export function App() {
  const { signedIn } = useSession();
  if (!signedIn) return <SignInPage />;
  return <Shell />;
}

function Shell() {
  const { signOut } = useSession();
  const who = useQuery({ queryKey: ['me'], queryFn: () => unwrap(me()) });

  // Signed in, but not a user of this shelf: the account exists, nobody has
  // given it access. Say so rather than showing an empty shelf.
  if (who.error) {
    return (
      <SignInPage.Refused message={(who.error as Error).message} onSignOut={signOut} />
    );
  }
  // Routing waits for the caller: an admin opening /users directly must not
  // be sent away while it is still unknown whether they are one.
  if (!who.data) {
    return (
      <Center mih="100dvh">
        <Loader size="sm" />
      </Center>
    );
  }
  const admin = who.data.isAdmin;

  return (
    <AppShell header={{ height: 56 }} padding="md">
      <AppShell.Header px="md">
        <Group h="100%" justify="space-between" wrap="nowrap">
          <Group gap="lg" wrap="nowrap">
            <UnstyledButton component={NavLink} to="/">
              <Wordmark />
            </UnstyledButton>
            {admin ? (
              <Group gap="xs" visibleFrom="xs">
                <Button component={NavLink} to="/" variant="subtle" size="compact-sm">
                  Shelf
                </Button>
                <Button component={NavLink} to="/users" variant="subtle" size="compact-sm">
                  Users & shares
                </Button>
              </Group>
            ) : null}
          </Group>
          <Group gap="sm" wrap="nowrap">
            <Text size="sm" c="dimmed" visibleFrom="sm" truncate maw={240}>
              {who.data.email}
            </Text>
            <Button variant="default" size="compact-sm" onClick={signOut}>
              Sign out
            </Button>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Main>
        <Routes>
          <Route path="/" element={<ShelfPage />} />
          <Route path="/users" element={admin ? <UsersPage /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}
